import { useSnackbar } from 'notistack';
import { useNetworkStatus } from '../context/NetworkStatusContext';
import { ErrorTypes } from '../types/query.types';
import { getNetworkErrorCode } from '../utils/utils';

const abortedErrorsMessages = [
  'signal is aborted without reason',
  'The operation was aborted.',
  'The user aborted a request.',
  'Fetch is aborted',
];

export const UNKNOWN_ERROR_MESSAGE = 'Something went wrong, Our engineers have been notified';

export const ERROR_STATUS = {
  BAD_REQUEST: 400,
  CONFLICT: 409,
} as const;

export const serializeError = (error: Error) => ({
  ...error,
  message: error?.message,
  name: error?.name,
  stack: error?.stack,
});

const checkTimeout = status => [502, 503, 504].includes(status);
export default function useHandleError(shouldShowError?) {
  const { enqueueSnackbar } = useSnackbar();
  const { isOnline } = useNetworkStatus();
  return (error?, others?) => {
    const status = getNetworkErrorCode(error);
    const errMessage =
      error?.response?.errors?.map(e => e?.message).join('\n\n') ??
      error?.message ??
      (Array.isArray(error) && error.map(e => e?.message || '').join(', ')) ??
      (typeof error === 'string' && error);

    if (abortedErrorsMessages.includes(errMessage?.toString()?.trim())) {
      return;
    }

    const isValidationError =
      [400, 406, 429].includes(status) ||
      error?.[0]?.extensions?.classification === 'BAD_REQUEST' ||
      error?.[0]?.extensions?.classification === 'ValidationError';

    if (!isOnline || !navigator.onLine) {
      console.warn({
        status: ErrorTypes.Offline,
        error: serializeError(error),
        ...others,
      });
      return;
    }

    console.error({
      status: isValidationError ? ErrorTypes.Validation : undefined,
      error: serializeError(error),
      ...others,
    });

    enqueueSnackbar(
      status === 403
        ? 'Access Denied'
        : checkTimeout(status)
          ? 'Request Timed out'
          : shouldShowError || isValidationError
            ? errMessage
            : UNKNOWN_ERROR_MESSAGE,
      {
        variant: status === 429 ? 'warning' : 'error',
        style: { whiteSpace: 'pre-line' },
      }
    );
  };
}
